import { isAxiosError } from 'axios';
import type { AxiosError } from 'axios';
import isEmpty from 'lodash/isEmpty';
import { ERROR_429 } from 'common/config/api';
import {
  AUTH_BACKUP_CODE_LENGTH,
  AUTH_CODE_LENGTH,
  AUTH_RECOVERY_CODE_LENGTH,
  ENTER_OTP_BACKUP_CODE_SUBTITLE,
  ENTER_OTP_SUBTITLE,
  ENTER_OTP_TITLE,
  OTP_TOO_MANY_ATTEMPTS_MESSAGE,
  OTP_TRY_AGAIN_LATER_MESSAGE,
} from 'common/constants/auth';
import type { AuthenticationInputFormState } from 'pages/Auth/components/AuthenticationInput';
import { extractResponseErrorMessage } from 'utilities';
import type { OtpTitles, OTPUriParams } from './OTP.types';

export const getOtpUriParams = (uri: string): OTPUriParams => {
  const queryString = uri.split('?')[1];

  if (!queryString) {
    return {};
  }

  return Array.from(new URLSearchParams(queryString).entries()).reduce<OTPUriParams>(
    (params, [key, value]) => ({
      ...params,
      [key.toLowerCase()]: value,
    }),
    {}
  );
};

export const normalizeOtpValue = (value: string) => value.replace(/\D/g, '').slice(0, AUTH_CODE_LENGTH);

export const normalizeBackupCodeValue = (value: string) => value
  .toLowerCase()
  .replace(/[^a-z2-7]/g, '')
  .slice(0, AUTH_BACKUP_CODE_LENGTH);

export const normalizeRecoveryCodeValue = (value: string) => value.replace(/\D/g, '').slice(0, AUTH_RECOVERY_CODE_LENGTH);

// Reads how long the server asked us to wait, which can arrive as a number, a string of
// seconds or a date, and is missing entirely unless the backend exposes the header.
const getRetryAfterSeconds = (error: AxiosError): number | null => {
  const headers = error.response?.headers as
    | { get?: (name: string) => unknown; [key: string]: unknown }
    | undefined;

  if (!headers) {
    return null;
  }

  let raw: unknown;

  if (typeof headers.get === 'function') {
    raw = headers.get('Retry-After') ?? headers.get('retry-after');
  }

  raw ??= headers['retry-after'] ?? headers['Retry-After'];

  if (Array.isArray(raw)) {
    [raw] = raw;
  }

  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? Math.max(0, Math.round(raw)) : null;
  }

  if (typeof raw !== 'string' || !raw.trim()) {
    return null;
  }

  const seconds = Number(raw);

  if (Number.isFinite(seconds)) {
    return Math.max(0, Math.round(seconds));
  }

  const dateMs = Date.parse(raw);

  return Number.isFinite(dateMs) ? Math.max(0, Math.round((dateMs - Date.now()) / 1000)) : null;
};

// Falls back to open-ended advice when the server didn't say how long to wait.
const formatRetryHint = (seconds: number | null): string => {
  if (seconds === null || seconds <= 0) {
    return OTP_TRY_AGAIN_LATER_MESSAGE;
  }

  if (seconds < 60) {
    return `Try again in ${seconds} ${seconds === 1 ? 'second' : 'seconds'}.`;
  }

  const minutes = Math.ceil(seconds / 60);

  return `Try again in about ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}.`;
};

// Turns a failed OTP request into something worth showing the user, rather than the raw
// "Request failed with status code 429" that the error carries by default.
export const normalizeOtpError = (
  error: unknown,
  fallbackMessage: string,
  throttledMessage: string = OTP_TOO_MANY_ATTEMPTS_MESSAGE
): string => {
  if (isAxiosError(error) && error.response?.status === Number(ERROR_429)) {
    return `${throttledMessage} ${formatRetryHint(getRetryAfterSeconds(error))}`;
  }

  // Trimmed first because a response with no usable body can yield an empty string, which
  // would render as a blank error.
  return extractResponseErrorMessage(error)?.trim() || fallbackMessage;
};

export const getOtpTitles = (useBackupCode: boolean): OtpTitles => {
  if (useBackupCode) {
    return {
      title: ENTER_OTP_TITLE,
      subtitle: ENTER_OTP_BACKUP_CODE_SUBTITLE,
    };
  }

  return {
    title: ENTER_OTP_TITLE,
    subtitle: ENTER_OTP_SUBTITLE,
  };
};

export const isOtpSubmitDisabled = (
  formState: AuthenticationInputFormState,
  isSubmitting: boolean
) => isSubmitting || isEmpty(formState.values) || !formState.isValid;

export const getFirstFieldMessage = (value?: string | string[]): string | undefined => {
  if (!value) {
    return undefined;
  }

  return Array.isArray(value) ? value[0] : value;
};
